/**
 * PLP Viewing Badge Component
 *
 * Displays "X people viewed" social proof on PLP product cards using
 * view counts from the analytics social-proof API.
 *
 * Configuration comes from the Collection Container section settings
 * (published as window.__bwwSocialProofConfig by the
 * 'social-proof-views-prefetch' snippet):
 * - days:     how many days back to count views (startDate=NdaysAgo)
 * - minViews: minimum views before a badge is shown
 * - maxDisplay: display cap; counts above it render as "N+"
 * The badge wording is rendered server-side in 'plp-viewing-badge.liquid';
 * this script only fills in the count and toggles visibility.
 *
 * Performance:
 * - ONE batched request fetches view counts for the entire catalog
 *   (metrics=itemsViewed grouped by itemName) instead of one request
 *   per product on the page.
 * - The request is kicked off at HTML parse time by the prefetch snippet
 *   (window.__bwwSocialProofViewsFetch), so the response is usually ready
 *   before this script runs.
 * - Results are cached in localStorage per day-range. The API reports
 *   trailing-day data, so counts only change ~daily; repeat PLP visits
 *   within the TTL make zero network requests.
 *
 * Counts are matched to cards by product title (GA4 item_name == Shopify
 * product title). Cards without a matching row, or below the threshold,
 * keep the badge hidden.
 */

// @ts-nocheck
(() => {
  const API_URL = "https://splittesting.acadia.io/analytics/social-proof";
  const PID = "289302657";
  const DEFAULT_DAYS = 1;
  const DEFAULT_MIN_VIEWS = 50;
  const DEFAULT_MAX_DISPLAY = 600;
  // Keep cache key prefix / TTL / request URL in sync with snippets/social-proof-views-prefetch.liquid
  const CACHE_KEY_PREFIX = "bww:social-proof:views:v2:";
  const CACHE_TTL = 3 * 60 * 60 * 1000;

  // Set to true to enable console logging
  const DEBUG = true;
  const log = (...args) => DEBUG && console.log("[PlpViewingBadge]", ...args);

  const getConfig = () => {
    const cfg = window.__bwwSocialProofConfig || {};
    const days = Math.max(parseInt(cfg.days, 10) || DEFAULT_DAYS, 1);
    // Floor of 1: even with the admin threshold at 0, products with no view
    // data must never render as "0 people viewed"
    const minViews = Math.max(Number.isFinite(Number(cfg.minViews)) ? Number(cfg.minViews) : DEFAULT_MIN_VIEWS, 1);
    // Cap for the displayed number — anything above renders as "N+"
    const maxDisplay = Math.max(parseInt(cfg.maxDisplay, 10) || DEFAULT_MAX_DISPLAY, 1);
    return { days, minViews, maxDisplay };
  };

  const requestUrlFor = days =>
    `${API_URL}?pid=${PID}&metrics=itemsViewed&dimensions=itemName&startDate=${days}daysAgo`;

  const cacheKeyFor = days => `${CACHE_KEY_PREFIX}${days}d`;

  const normalizeTitle = title => (title || "").toLowerCase().replace(/\s+/g, " ").trim();

  /**
   * PLP Viewing Badge Custom Element
   * Renders a "X people viewed" message on a product card
   */
  class PlpViewingBadge extends HTMLElement {
    /**
     * @param {number} count - Number of views for this product
     * @param {number} [maxDisplay] - Cap; counts above it render as "N+"
     */
    setCount(count, maxDisplay) {
      const countEl = this.querySelector(".sp-viewing-badge__count");
      if (countEl) {
        const capped = maxDisplay && count > maxDisplay;
        const next = Number(capped ? maxDisplay : count).toLocaleString("en-US") + (capped ? "+" : "");
        if (countEl.textContent !== next) countEl.textContent = next;
      }
    }

    /**
     * Every class mutation below is guarded by a state check. `classList.add`/
     * `remove` write the class attribute even when the token set is unchanged,
     * and a MutationObserver watching `class` fires on any write — so an
     * unguarded toggle here re-triggers the grid observer and loops forever.
     */
    show() {
      this.clearLoading();
      if (this.classList.contains("hidden")) this.classList.remove("hidden");
      if (this.style.display !== "flex") this.style.display = "flex";
    }

    hide() {
      this.clearLoading();
      if (!this.classList.contains("hidden")) this.classList.add("hidden");
      if (this.style.display !== "") this.style.display = "";
    }

    /**
     * Shows the badge with an orange shimmer over the count while the
     * API data loads (same pattern as the cart loyalty points shimmer)
     */
    setLoading() {
      if (!this.classList.contains("sp-viewing-badge--loading")) {
        this.classList.add("sp-viewing-badge--loading");
      }
      if (this.classList.contains("hidden")) this.classList.remove("hidden");
      if (this.style.display !== "flex") this.style.display = "flex";
    }

    clearLoading() {
      if (this.classList.contains("sp-viewing-badge--loading")) {
        this.classList.remove("sp-viewing-badge--loading");
      }
    }
  }

  if (!customElements.get("plp-viewing-badge")) {
    customElements.define("plp-viewing-badge", PlpViewingBadge);
  }

  /**
   * PLP Viewing Badge Manager (Singleton)
   *
   * Loads the view-count map once, applies it to every badge on the page,
   * and watches the grid for cards added by load-more or facet filtering.
   */
  class PlpViewingBadgeManager {
    /**
     * @param {{days: number, minViews: number, maxDisplay: number}} config
     */
    constructor(config) {
      this.days = config.days;
      this.minViews = config.minViews;
      this.maxDisplay = config.maxDisplay;
      this._counts = null; // Map<normalized product title, view count>
      this._observer = null;
    }

    async init() {
      log("Config:", { days: this.days, minViews: this.minViews, maxDisplay: this.maxDisplay });

      // Only shimmer when a network request is actually needed — cached
      // loads apply instantly with no loading state
      if (!this._readCache()) {
        this._setLoadingAll();
      }

      this._counts = await this._loadCounts();
      if (!this._counts || this._counts.size === 0) {
        log("No view counts available, badges stay hidden");
        this._clearLoadingAll();
        return;
      }
      this.applyAll();
      this._observeGrid();
    }

    _setLoadingAll() {
      document.querySelectorAll("plp-viewing-badge[data-product-title]").forEach(badge => {
        if (typeof badge.setLoading === "function") badge.setLoading();
      });
    }

    _clearLoadingAll() {
      document.querySelectorAll("plp-viewing-badge").forEach(badge => {
        if (typeof badge.hide === "function") badge.hide();
      });
    }

    destroy() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    _readCache() {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKeyFor(this.days)) || "null");
        if (cached && cached.ts && Date.now() - cached.ts < CACHE_TTL && cached.counts) {
          return cached.counts;
        }
      } catch (error) {
        // Corrupt cache — fall through to refetch
      }
      return null;
    }

    _writeCache(counts) {
      try {
        localStorage.setItem(cacheKeyFor(this.days), JSON.stringify({ ts: Date.now(), counts }));
      } catch (error) {
        // Quota exceeded / private mode — non-fatal
      }
    }

    /**
     * @returns {Promise<Map<string, number>|null>}
     */
    async _loadCounts() {
      const cached = this._readCache();
      if (cached) {
        log("Using cached counts (no request made):", Object.keys(cached).length, "products |", cached);
        return new Map(Object.entries(cached));
      }

      try {
        const requestUrl = requestUrlFor(this.days);
        // Reuse the request started at parse time by social-proof-views-prefetch.liquid
        const pending = window.__bwwSocialProofViewsFetch;
        log("Request:", requestUrl, pending ? "(started early by prefetch snippet)" : "(started now)");
        const data = pending ? await pending : await fetch(requestUrl).then(r => (r.ok ? r.json() : null));
        log("Response:", data);

        const rows = Array.isArray(data?.value) ? data.value : [];
        log("API rows:", rows.length);
        if (rows.length === 0) return null;

        const counts = {};
        for (const row of rows) {
          const views = parseInt(row.itemsViewed, 10);
          if (row.itemName && views > 0) {
            counts[normalizeTitle(row.itemName)] = views;
          }
        }
        this._writeCache(counts);
        return new Map(Object.entries(counts));
      } catch (error) {
        console.error("[PlpViewingBadge] Error fetching social proof data:", error);
        return null;
      }
    }

    /**
     * Apply counts to all unprocessed badges within root
     * @param {ParentNode} [root]
     */
    applyAll(root = document) {
      if (!this._counts) return;
      const badges = root.querySelectorAll?.("plp-viewing-badge[data-product-title]") || [];
      badges.forEach(badge => this._applyOne(badge));
    }

    _applyOne(badge) {
      if (!this._counts || typeof badge.setCount !== "function") return;

      // Idempotent enforcement: facet filtering morphs the grid and resets
      // badge markup to the server HTML (hidden), so always recompute the
      // desired state instead of tracking an "applied" flag
      const count = this._counts.get(normalizeTitle(badge.dataset.productTitle)) || 0;
      if (count > 0 && count >= this.minViews) {
        if (badge.classList.contains("hidden")) {
          log("Showing badge:", badge.dataset.productTitle, "|", count, "views");
        }
        badge.setCount(count, this.maxDisplay);
        badge.show();
      } else {
        badge.hide();
      }
    }

    /**
     * Watch for cards added by load-more, and for facet filtering re-renders
     * that morph existing badges back to their server-rendered (hidden) state
     */
    _observeGrid() {
      if (this._observer) this._observer.disconnect();

      this._observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          // Morph reset a badge's class attribute — re-enforce its state
          if (mutation.type === "attributes") {
            if (mutation.target.tagName === "PLP-VIEWING-BADGE") {
              this._applyOne(mutation.target);
            }
            continue;
          }
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches?.("plp-viewing-badge")) {
              this._applyOne(node);
            } else {
              this.applyAll(node);
            }
          }
        }

        // Discard the records our own _applyOne writes just queued. Without
        // this, re-enforcing a badge's class re-enters this callback on the
        // next microtask and the queue never drains — the main thread locks
        // up and facet filtering never finishes rendering.
        this._observer?.takeRecords();
      });

      // Scope to the results grid: observing `class` across all of document.body
      // means every loading-state toggle on the page (filter drawer, buttons,
      // facet chips) wakes this callback during filtering.
      const scope = document.querySelector("results-list") || document.body;
      this._observer.observe(scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  }

  // Singleton instance
  let managerInstance = null;

  function boot() {
    const config = getConfig();

    if (managerInstance) {
      if (managerInstance.days !== config.days) {
        // Day range changed (theme editor) — needs a fresh dataset
        managerInstance.destroy();
        managerInstance = new PlpViewingBadgeManager(config);
        managerInstance.init();
      } else {
        // Section re-render replaces badge nodes, so new nodes re-evaluate
        // against the (possibly updated) threshold and cap
        managerInstance.minViews = config.minViews;
        managerInstance.maxDisplay = config.maxDisplay;
        managerInstance.applyAll();
      }
      return;
    }

    managerInstance = new PlpViewingBadgeManager(config);
    managerInstance.init();
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Re-initialize on Shopify section loads (theme editor)
  document.addEventListener("shopify:section:load", boot);

  // Expose for debugging
  window.__PlpViewingBadge__ = {
    PlpViewingBadge,
    PlpViewingBadgeManager,
    boot,
    getInstance: () => managerInstance,
  };
})();
